/**
 * /ny-hemsida — Elido Google Ads-landningssida
 * ----------------------------------------------
 * Fokuserad säljsida för kalla besökare som aktivt söker en ny företags-
 * hemsida. Fristående layout — vi använder INTE NexusLayout eftersom
 * den har en full site-header/footer som konkurrerar med sidans CTA.
 *
 * Regler i toppen av filen (så framtida diffar inte råkar bryta något):
 *   • Primär CTA: "Få ett förslag" — scrollar till #formular
 *   • Sekundär CTA: "Se våra kundcase" — scrollar till #kundcase
 *   • Ingen fullständig nav-meny i header. Ingen widget/chatt/CTA-lager.
 *   • Alla dataLayer-events går via utils/nyHemsidaTracking.js.
 *   • `noindex,follow` + self-referencing canonical sätts i <Helmet>.
 *     GlobalSEOHead skulle annars sätta index,follow för denna route
 *     — se justeringen i GlobalSEOHead.jsx.
 *   • Formulär postas till POST /api/leads/ny-hemsida och skickar
 *     `attribution`-objekt tillsammans med fälten. Success-eventet
 *     skickas ENDAST efter verifierat OK-svar.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ExternalLink,
  Loader2,
  Mail,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/utils/api";
import CookieConsent from "@/components/CookieConsent";
import { CONSENT_EVENTS } from "@/utils/cookieConsent";
import { dedupeSeoTags } from "@/utils/seoHeadDedupe";
import NexusFooter from "@/components/NexusFooter";
import {
  captureAttribution,
  getAttribution,
  trackCaseClick,
  trackCtaClick,
  trackFormStart,
  trackFormSuccess,
  trackGoogleReviewsClick,
} from "@/utils/nyHemsidaTracking";
import { getConsent, getConsentLeadFields } from "@/utils/cookieConsent";

// ─── Konstanter ────────────────────────────────────────────────────────
const ELIDO_LOGO_BLUE = "/img/ny-hemsida/elido-blue.png";
const GOOGLE_REVIEWS_URL = "https://share.google/9RTbLJTbmmTSv7A83";

// Endpoint för leads — dedikerad för denna kampanj
const LEAD_ENDPOINT = `${API_URL}/api/leads/ny-hemsida`;

// ─── Hjälpare ─────────────────────────────────────────────────────────
function smoothScrollTo(hashHref) {
  if (!hashHref || !hashHref.startsWith("#")) return false;
  const id = hashHref.slice(1);
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", hashHref);
  }
  return true;
}

// ─── CTA-knapp ────────────────────────────────────────────────────────
function CtaButton({
  href,
  label,
  variant = "primary",
  size = "md",
  testId,
  onClick,
  trackingLocation,
  className = "",
}) {
  const sizes = {
    md: "px-6 py-3 text-[14px]",
    lg: "px-7 py-3.5 text-[15px]",
  };
  const variants = {
    primary:
      "bg-[#F5A65B] hover:bg-[#eb9843] text-[#0b1f27] shadow-[0_10px_28px_-8px_rgba(245,166,91,0.55)] focus-visible:ring-[#F5A65B]/40",
    secondary:
      "bg-white/95 hover:bg-white text-[#0e2f36] border border-[#0e2f36]/10 focus-visible:ring-[#0e2f36]/20",
    dark:
      "bg-[#0e2f36] hover:bg-[#164a55] text-white shadow-[0_10px_28px_-8px_rgba(14,47,54,0.45)] focus-visible:ring-[#0e2f36]/30",
    ghost:
      "bg-transparent hover:bg-white/10 text-white border border-white/25 focus-visible:ring-white/40",
  };
  const base = `inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-tight transition-all duration-200 hover:translate-y-[-1px] focus:outline-none focus-visible:ring-4 ${sizes[size] || sizes.md} ${variants[variant] || variants.primary} ${className}`;
  const content = (
    <>
      <span>{label}</span>
      <ArrowRight className="w-4 h-4" aria-hidden="true" />
    </>
  );

  const handle = (e) => {
    if (trackingLocation) trackCtaClick(trackingLocation);
    if (typeof onClick === "function") onClick(e);
    if (href && href.startsWith("#") && smoothScrollTo(href)) {
      e.preventDefault();
    }
  };

  if (href && href.startsWith("/")) {
    return (
      <Link to={href} className={base} data-testid={testId} onClick={handle}>
        {content}
      </Link>
    );
  }
  return (
    <a href={href || "#formular"} className={base} data-testid={testId} onClick={handle}>
      {content}
    </a>
  );
}

// ─── Trust logos ──────────────────────────────────────────────────────
// Same logo list as the main site's /-page trust cloud (NexusHomePage).
// Kept inline here so the landing page renders without a server round-
// trip. Per-logo optical size compensation keeps every logo visually
// balanced inside identical outer boxes.
const CUSTOMER_LOGOS = [
  { name: "Trädfällarna Laholm", logo_url: "/img/logos/rena-tjanster.png", url: "/case-tradfallarna-i-laholm" },
  { name: "Rena Tjänster", logo_url: "/img/logos/tradfallarna.png", url: "/case-renatjanster" },
  { name: "Gränsbygdens Köpcentrum", logo_url: "/img/logos/gransbygden.png", url: "/case-gransbygdens-chatbot" },
  { name: "Bentech", logo_url: "/img/logos/bentech.png", url: "/case-bentech-chatbot" },
  { name: "SVEA Ytservice", logo_url: "/img/logos/ic-identity.png", url: "/kundcase-svea-ytservice" },
  { name: "IC Identity", logo_url: "/img/logos/svea-ytservice.png", url: "/case-icidentity" },
  { name: "Solgläntans Redovisningsbyrå", logo_url: "/img/logos/solglantan.png", url: "/case-solglantansredovisningsbyra" },
  { name: "Aljo Bygg AB", logo_url: "/img/logos/aljo-bygg.png", url: "/case-aljobygg" },
  { name: "Rent & Ro AB", logo_url: "/img/logos/rent-och-ro.png", url: "/case-rentoro" },
];
const LOGO_SIZE_OVERRIDES = {
  "tradfallarna.png": "max-h-10 lg:max-h-12",
  "rena-tjanster.png": "max-h-12 lg:max-h-14",
  "gransbygden.png": "max-h-6 lg:max-h-8",
  "bentech.png": "max-h-6 lg:max-h-7",
  "svea-ytservice.png": "max-h-12 lg:max-h-14",
  "ic-identity.png": "max-h-8 lg:max-h-10",
  "solglantan.png": "max-h-7 lg:max-h-9",
  "aljo-bygg.png": "max-h-6 lg:max-h-8",
  "rent-och-ro.png": "max-h-11 lg:max-h-14",
};
function logoSizeClass(url) {
  if (!url) return "max-h-8 lg:max-h-9";
  const match = Object.keys(LOGO_SIZE_OVERRIDES).find((k) => url.endsWith(k));
  return match ? LOGO_SIZE_OVERRIDES[match] : "max-h-8 lg:max-h-9";
}

function TrustLogoCloud() {
  return (
    <section
      className="bg-white py-14 lg:py-16 border-b border-slate-100"
      data-testid="ny-hemsida-trust-logos"
    >
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="text-center">
          <h2
            className="text-2xl sm:text-[1.75rem] lg:text-[2rem] font-bold text-[#0b1f27] tracking-tight"
            data-testid="ny-hemsida-trust-logos-title"
          >
            Några av våra kunder
          </h2>
        </div>
        <div
          className="mt-8 lg:mt-10 flex flex-wrap justify-center gap-3 sm:gap-4"
          data-testid="ny-hemsida-trust-logos-grid"
        >
          {CUSTOMER_LOGOS.map((c, idx) => {
            const boxClass =
              "group flex items-center justify-center rounded-lg bg-slate-50 border border-slate-100 hover:border-slate-200 transition-colors w-[calc(50%-6px)] sm:w-[172px] lg:w-[176px] h-14 lg:h-[68px] px-4";
            const img = (
              <img
                src={c.logo_url}
                alt={c.name}
                loading="lazy"
                className={
                  "max-w-full w-auto h-auto object-contain " +
                  logoSizeClass(c.logo_url)
                }
              />
            );
            const testId = `ny-hemsida-trust-logo-${idx}`;
            if (c.url && c.url.startsWith("/")) {
              return (
                <Link key={idx} to={c.url} className={boxClass} title={c.name} data-testid={testId}>
                  {img}
                </Link>
              );
            }
            if (c.url) {
              return (
                <a
                  key={idx}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={boxClass}
                  title={c.name}
                  data-testid={testId}
                >
                  {img}
                </a>
              );
            }
            return (
              <div key={idx} className={boxClass} title={c.name} data-testid={testId}>
                {img}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Header ───────────────────────────────────────────────────────────
function LandingHeader() {
  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200/70"
      data-testid="ny-hemsida-header"
    >
      <div className="max-w-6xl mx-auto pl-4 pr-3 sm:pl-5 sm:pr-5 lg:px-8 h-20 lg:h-24 flex items-center justify-between gap-3 sm:gap-4">
        <a
          href="https://elido.nu/"
          className="flex items-center gap-2 shrink-0"
          data-testid="ny-hemsida-logo-link"
          aria-label="Elido — startsida"
        >
          <img
            src={ELIDO_LOGO_BLUE}
            alt="Elido"
            className="h-10 sm:h-14 w-auto"
            width="236"
            height="120"
          />
        </a>
        <a
          href="#formular"
          onClick={(e) => {
            trackCtaClick("header");
            if (smoothScrollTo("#formular")) e.preventDefault();
          }}
          className="inline-flex items-center justify-center gap-1.5 rounded-full font-semibold tracking-tight bg-[#0e2f36] hover:bg-[#164a55] text-white shadow-[0_10px_28px_-8px_rgba(14,47,54,0.45)] transition-all duration-200 hover:translate-y-[-1px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#0e2f36]/30 px-3.5 py-2 text-[13px] sm:px-5 sm:py-2.5 sm:text-[14px]"
          data-testid="header-cta"
        >
          <span>Få mitt prisförslag</span>
          <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
        </a>
      </div>
    </header>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background:
          "radial-gradient(1200px 500px at 90% -10%, rgba(93,188,203,0.10) 0%, transparent 60%)," +
          "radial-gradient(900px 400px at 5% 100%, rgba(14,47,54,0.06) 0%, transparent 55%)," +
          "linear-gradient(180deg, #FBF7F2 0%, #F6F0E7 100%)",
      }}
      data-testid="ny-hemsida-hero"
    >
      {/* Ambient bakgrund – mjuka, långsamt rörliga ljusblobbar */}
      <div
        className="nyh-blob nyh-blob-1 pointer-events-none absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full blur-3xl bg-[radial-gradient(circle_at_center,rgba(93,188,203,0.22),transparent_70%)]"
        aria-hidden="true"
      />
      <div
        className="nyh-blob nyh-blob-2 pointer-events-none absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full blur-3xl bg-[radial-gradient(circle_at_center,rgba(58,138,152,0.16),transparent_70%)]"
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-6xl mx-auto px-5 lg:px-8 pt-10 lg:pt-16 pb-14 lg:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-6 max-w-xl">
            <div
              className="nyh-hero-in nyh-d1 inline-flex items-center text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-5 px-3 py-1.5 rounded-full bg-white/60 border border-[#255E69]/15 backdrop-blur"
              data-testid="hero-eyebrow"
            >
              Företagssida med fast pris • Ni äger sidan
            </div>
            <h1
              className="nyh-hero-in nyh-d2 text-4xl sm:text-5xl lg:text-[3.4rem] font-bold text-[#0b1f27] leading-[1.04] tracking-tight"
              data-testid="hero-title"
            >
              En professionell hemsida för ert företag – från{" "}
              <span className="whitespace-nowrap">2&nbsp;990&nbsp;kr</span>
            </h1>
            <p className="nyh-hero-in nyh-d3 mt-5 text-[16px] sm:text-[17px] lg:text-[18px] text-[#334155] leading-relaxed">
              Vi bygger, mobilanpassar och publicerar. Ni får ett fast pris innan
              start, direktkontakt med oss som bygger och ingen bindningstid.
            </p>

            <div className="nyh-hero-in nyh-d4 mt-8 w-full sm:w-fit max-w-full">
              <div className="flex flex-col sm:flex-row gap-3">
                <CtaButton
                  href="#formular"
                  label="Få mitt prisförslag"
                  variant="primary"
                  size="lg"
                  testId="hero-cta-primary"
                  trackingLocation="hero_primary"
                  className="w-full sm:w-auto"
                />
                <CtaButton
                  href="#priser"
                  label="Se exakt vad som ingår"
                  variant="secondary"
                  size="md"
                  testId="hero-cta-secondary"
                  trackingLocation="hero_secondary"
                  className="w-full sm:w-auto"
                />
              </div>
              <p className="mt-3 text-[13px] text-[#475569]" data-testid="hero-microcopy">
                Kostnadsfritt och utan köpkrav. Namn och e-post räcker.
              </p>
            </div>

            <ul
              className="nyh-hero-in nyh-d5 mt-8 grid grid-cols-2 gap-x-6 gap-y-2.5 text-[13.5px] text-[#334155] max-w-md"
              data-testid="hero-trust-row"
            >
              {[
                "5/5 på Google",
                "Fast pris",
                "Ni äger sidan",
                "Ingen bindningstid",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#3A8A98] mt-[3px] shrink-0" aria-hidden="true" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Case stack – hero visual */}
          <div className="nyh-hero-visual lg:col-span-6 relative">
            <HeroCaseStack />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Hero-visual: fotot på David & Filip. Naturlig, premium presentation utan
 * browser-ram eller dekorativa mockup-element. Mobil använder en tajtare
 * crop av samma bild så att båda personerna syns tydligt. Under bilden en
 * mycket diskret trust-rad.
 */
function HeroCaseStack() {
  return (
    <div data-testid="hero-case-stack">
      <figure className="m-0" data-testid="hero-people-photo">
        <picture>
          <source
            media="(max-width: 1023px)"
            type="image/webp"
            srcSet="/img/ny-hemsida/david-filip-mobile-640.webp 640w, /img/ny-hemsida/david-filip-mobile-960.webp 960w"
            sizes="92vw"
          />
          <img
            src="/img/ny-hemsida/david-filip-1280.webp"
            srcSet="/img/ny-hemsida/david-filip-800.webp 800w, /img/ny-hemsida/david-filip-1280.webp 1280w, /img/ny-hemsida/david-filip-1920.webp 1920w"
            sizes="(min-width: 1024px) 45vw, 92vw"
            alt="David Siekas och Filip Imberg, Elido AB, i samtal"
            width="1280"
            height="960"
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            className="block w-full h-auto rounded-2xl"
          />
        </picture>
        <figcaption
          className="mt-3 text-[12.5px] text-[#64748B] leading-relaxed"
          data-testid="hero-people-trustline"
        >
          David &amp; Filip – direktkontakt med oss som bygger.
        </figcaption>
      </figure>
    </div>
  );
}

/**
 * Diskret premium browser-ram. Tunn kant, ingen tung skugga, inga
 * dekorativa element. Trafikljus-prickarna är dämpade och små —
 * inte en Mac/Safari-imitation. URL:en visas som lugn monospace-etikett
 * mitt i toppfältet från sm och uppåt.
 */
function BrowserFrame({ url, compact = false, children }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-[0_10px_28px_-18px_rgba(15,42,48,0.18)]">
      <div
        className={`flex items-center gap-1.5 border-b border-slate-200 bg-[#F7F5F1] ${
          compact ? "px-3 py-1.5" : "px-3.5 py-2"
        }`}
      >
        <span className="w-2 h-2 rounded-full bg-slate-300" aria-hidden="true" />
        <span className="w-2 h-2 rounded-full bg-slate-300" aria-hidden="true" />
        <span className="w-2 h-2 rounded-full bg-slate-300" aria-hidden="true" />
        {url && (
          <div className="flex-1 flex justify-center min-w-0">
            <span
              className={`hidden sm:inline-flex items-center max-w-[85%] truncate text-slate-500 font-mono tracking-tight px-2 py-[1px] rounded bg-white/70 border border-slate-200/70 ${
                compact ? "text-[10px]" : "text-[11px]"
              }`}
            >
              {url}
            </span>
          </div>
        )}
      </div>
      <div className="block">{children}</div>
    </div>
  );
}

// ─── Google trust strip ───────────────────────────────────────────────
function TrustStrip() {
  return (
    <section
      className="bg-white border-y border-slate-200/70"
      data-testid="ny-hemsida-trust-strip"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-5 lg:px-8 py-6 lg:py-7">
        <div className="flex flex-col items-center text-center gap-4 sm:flex-row sm:text-left sm:items-center sm:justify-between sm:gap-6">
          <div className="flex flex-col items-center sm:items-start gap-2">
            {/* 5/5 på Google — behåll tydligt */}
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackGoogleReviewsClick("trust_strip")}
              className="flex items-center gap-2 group"
              data-testid="google-reviews-link"
            >
              <span className="flex items-center gap-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#F5A65B] text-[#F5A65B]"
                    aria-hidden="true"
                  />
                ))}
              </span>
              <span className="text-[14px] sm:text-[15px] font-semibold text-[#0b1f27] group-hover:text-[#255E69] transition-colors whitespace-nowrap">
                5/5 på Google
              </span>
              <ExternalLink
                className="w-3.5 h-3.5 text-[#94a3b8] group-hover:text-[#255E69] transition-colors"
                aria-hidden="true"
              />
            </a>
            {/* Kort social-proof-rad — inte en upprepad feature-lista */}
            <p className="text-[13.5px] sm:text-[14.5px] text-[#475569] leading-relaxed max-w-xl">
              Riktiga företag med publicerade hemsidor. Fast pris, ni äger sidan
              och direktkontakt med oss som bygger.
            </p>
          </div>
          <div className="shrink-0">
            <CtaButton
              href="#formular"
              label="Få mitt prisförslag"
              variant="secondary"
              size="md"
              testId="trust-strip-cta"
              trackingLocation="trust_strip"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Kundcase (tre publicerade kundsidor) ────────────────────────────
function CustomerCases() {
  // Tre verkliga, publicerade kundsidor — visas tidigt som konkret proof.
  const cases = [
    {
      id: "tradfallarna",
      name: "Trädfällarna Laholm",
      url: "https://xn--trdfllarna-r5ac.nu/",
      urlLabel: "tradfallarna.nu",
      category: "Trädfällning",
      did: "Ny hemsida med tydligare tjänster och en rak väg till offert.",
      src: "tradfallarna-live",
      h: 800,
    },
    {
      id: "rena",
      name: "Rena Tjänster",
      url: "https://renatjanster.se/",
      urlLabel: "renatjanster.se",
      category: "B2B-städning",
      did: "Företagssida byggd för fler företagskunder — tydliga tjänster och en rak väg till offert.",
      src: "case-rena",
      h: 720,
    },
    {
      id: "ramsberg",
      name: "Sara Ramsberg Psykoterapi",
      url: "https://ramsbergpsykoterapi.se/",
      urlLabel: "ramsbergpsykoterapi.se",
      category: "Psykoterapi",
      did: "Lugn och förtroendeingivande sida med en enkel väg att ta kontakt.",
      src: "case-ramsberg",
      h: 960,
    },
  ];

  return (
    <section
      id="kundcase"
      className="bg-white py-16 lg:py-28 scroll-mt-24"
      data-testid="ny-hemsida-cases"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
            Tre publicerade kundsidor
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold text-[#0b1f27] tracking-tight leading-[1.1]">
            Riktiga företag. Riktiga sidor.
          </h2>
          <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">
            Olika verksamheter, olika mål. Alla tre är byggda av oss – och
            publicerade och live idag.
          </p>
        </div>

        <div className="mt-10 lg:mt-14 grid gap-8 lg:gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {cases.map((c) => (
            <article
              key={c.id}
              className="group flex flex-col"
              data-testid={`case-${c.id}`}
            >
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCaseClick(c.id)}
                className="block transition-transform duration-300 hover:-translate-y-1"
                data-testid={`case-${c.id}-preview`}
              >
                <BrowserFrame url={c.urlLabel} compact>
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    <img
                      src={`/img/ny-hemsida/${c.src}-1280.webp`}
                      srcSet={`/img/ny-hemsida/${c.src}-800.webp 800w, /img/ny-hemsida/${c.src}-1280.webp 1280w`}
                      sizes="(min-width: 1024px) 30vw, 100vw"
                      alt={`Hemsidan ${c.name} — byggd av Elido`}
                      loading="lazy"
                      decoding="async"
                      width="1280"
                      height={c.h}
                      className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                    />
                  </div>
                </BrowserFrame>
              </a>
              <div className="mt-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold tracking-[0.12em] uppercase text-[#255E69] bg-[#255E69]/8 border border-[#255E69]/15 rounded-full px-2 py-0.5">
                    Publicerad sida
                  </span>
                  <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#8a6f4a]">
                    {c.category}
                  </span>
                </div>
                <h3 className="mt-2 text-xl lg:text-[1.35rem] font-bold text-[#0b1f27] leading-snug">
                  {c.name}
                </h3>
                <p className="mt-2 text-[14.5px] text-[#334155] leading-relaxed">
                  {c.did}
                </p>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCaseClick(c.id)}
                  className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#255E69] hover:text-[#0e2f36] transition-colors"
                  data-testid={`case-${c.id}-link`}
                >
                  <span>Besök hemsidan</span>
                  <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Prissektion (inkluderar "Vad ingår" per nivå) ────────────────────
function PricingSection() {
  const includedInSimple = [
    "Specialdesignad från grunden",
    "En sammanhållen sida",
    "Hjälp med struktur och innehåll",
    "Mobilanpassning",
    "Kontaktformulär",
    "Teknisk SEO-grund",
    "Publicering och domänhjälp",
  ];

  const includedInLarger = [
    "Flera undersidor",
    "Större innehållsstruktur",
    "Flera formulär",
    "Blogg eller nyheter",
    "Flera språk",
    "Utökad SEO-grund",
    "Integrationer och specialfunktioner vid behov",
  ];

  return (
    <section
      id="priser"
      className="bg-[#FBF7F2] py-16 lg:py-24 scroll-mt-24"
      data-testid="ny-hemsida-pricing"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
            Priser & vad ni får
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold text-[#0b1f27] tracking-tight leading-[1.12]">
            Tydligt vad ni får – från 2 990 kr
          </h2>
          <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">
            Vi arbetar med tydliga fasta priser. En enklare specialdesignad
            företagssida börjar från 2 990 kr och en större sida från 5 990 kr.
            Behöver ni fler sidor, funktioner eller mer innehåll får ni ett fast
            pris innan något arbete börjar.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-6 items-stretch">
          {/* Enkel */}
          <div
            id="enkel-foretagshemsida"
            className="scroll-mt-24 bg-white rounded-2xl p-6 lg:p-8 border border-slate-200 shadow-sm flex flex-col"
            data-testid="pricing-simple"
          >
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
              Enkel företagshemsida
            </div>
            <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
              <span className="text-3xl lg:text-[2.4rem] font-bold text-[#0b1f27] tracking-tight">
                Från 2 990 kr
              </span>
            </div>
            <p className="mt-3 text-[14.5px] text-[#334155] leading-relaxed">
              För mindre företag som behöver en professionell och komplett
              närvaro online.
            </p>
            <ul
              className="mt-5 space-y-2"
              data-testid="pricing-simple-included"
            >
              {includedInSimple.map((t, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[14px] text-[#1e293b] leading-snug"
                >
                  <Check className="w-3.5 h-3.5 text-[#3A8A98] mt-[3px] shrink-0" aria-hidden="true" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-4 border-t border-slate-200 text-[13px] text-[#475569]">
              Drift & support från 299 kr/mån
            </div>
            <div className="mt-5">
              <CtaButton
                href="#formular"
                label="Få mitt prisförslag"
                variant="primary"
                size="md"
                testId="pricing-simple-cta"
                trackingLocation="pricing_simple"
              />
            </div>
          </div>

          {/* Större */}
          <div
            className="relative bg-gradient-to-br from-[#0e2f36] to-[#164a55] text-white rounded-2xl p-6 lg:p-8 shadow-[0_30px_60px_-24px_rgba(15,42,48,0.35)] flex flex-col"
            data-testid="pricing-larger"
          >
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5DBCCB] mb-3">
              Större företagshemsida
            </div>
            <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
              <span className="text-3xl lg:text-[2.4rem] font-bold tracking-tight">Från 5 990 kr</span>
            </div>
            <p className="mt-3 text-[14.5px] text-white/85 leading-relaxed">
              För företag som behöver mer innehåll, fler sidor eller
              funktioner.
            </p>
            <ul className="mt-5 space-y-2">
              {includedInLarger.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px] text-white/85 leading-snug">
                  <Check className="w-3.5 h-3.5 text-[#5DBCCB] mt-[3px] shrink-0" aria-hidden="true" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 pt-4 border-t border-white/10 text-[12.5px] text-white/60 leading-relaxed">
              Exakt omfattning och pris anpassas efter projektet.
            </p>
            <div className="mt-5">
              <CtaButton
                href="#formular"
                label="Diskutera ert projekt"
                variant="ghost"
                size="md"
                testId="pricing-larger-cta"
                trackingLocation="pricing_larger"
              />
            </div>
          </div>
        </div>

        {/* Diskret väg för mer omfattande projekt */}
        <div
          className="mt-8 rounded-2xl border border-slate-200 bg-white/60 p-5 lg:p-6 text-center"
          data-testid="pricing-extended-note"
        >
          <div className="text-[13.5px] font-semibold text-[#0b1f27]">
            Behöver ni något mer omfattande?
          </div>
          <p className="mt-1 text-[13.5px] text-[#475569] leading-relaxed">
            E-handel, integrationer, bokningssystem eller andra speciallösningar
            offereras separat. Vill ni synas bättre på Google kan löpande SEO
            läggas till som tillval.
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── Därför Elido (kombinerar arbetssätt + ägande) ───────────────────
function WhyElidoSection() {
  const points = [
    "Fast pris innan arbetet börjar – inga överraskningar",
    "Ni äger hemsidan, domänen och era konton",
    "Ingen bindningstid",
    "Direktkontakt med oss som bygger – ingen säljavdelning",
  ];
  return (
    <section className="bg-white py-14 lg:py-24" data-testid="ny-hemsida-why">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-4 px-3 py-1.5 rounded-full bg-[#255E69]/5 border border-[#255E69]/15">
            <ShieldCheck className="w-3 h-3" />
            Trygga villkor
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold text-[#0b1f27] tracking-tight leading-[1.12]">
            Inga överraskningar – ni bestämmer
          </h2>
          <p className="mt-4 text-[15px] sm:text-[16px] text-[#334155] leading-relaxed">
            Ni ska kunna säga ja med lugn mage. Därför får ni priset innan vi
            börjar, äger sidan själva och slipper bindningstid. Och ni pratar
            hela tiden direkt med oss som bygger.
          </p>
        </div>

        <div className="lg:col-span-7">
          <ul
            className="rounded-2xl border border-slate-200 bg-[#FBF7F2] divide-y divide-slate-200/70 overflow-hidden"
            data-testid="why-list"
          >
            {points.map((p, i) => (
              <li
                key={i}
                className="flex items-center gap-3.5 px-5 py-3.5 lg:px-6"
                data-testid={`why-point-${i}`}
              >
                <span className="w-6 h-6 rounded-full bg-[#3A8A98]/12 border border-[#3A8A98]/30 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-[#255E69]" aria-hidden="true" />
                </span>
                <span className="text-[14.5px] sm:text-[15px] text-[#0b1f27] font-medium leading-snug">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Process (kort) ──────────────────────────────────────────────────
function ProcessSection() {
  const steps = [
    {
      num: "01",
      title: "Ni skickar en kort förfrågan",
      text: "Berätta kort om företaget och vad ni behöver. Det tar någon minut.",
    },
    {
      num: "02",
      title: "Ni får rekommendation och fast pris",
      text: "Vi går igenom behovet och återkommer med ett tydligt upplägg och ett fast pris innan något arbete börjar.",
    },
    {
      num: "03",
      title: "Vi bygger, mobilanpassar och publicerar",
      text: "När allt känns rätt färdigställer vi sidan, mobilanpassar den och hjälper till med publiceringen.",
    },
  ];
  return (
    <section
      className="bg-gradient-to-b from-[#FBF7F2] to-white py-14 lg:py-20"
      data-testid="ny-hemsida-process"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-9">
          <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
            Så går det till
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.4rem] font-bold text-[#0b1f27] tracking-tight leading-[1.12]">
            Tre steg från förfrågan till publicerad hemsida
          </h2>
        </div>
        <div className="grid gap-4 lg:gap-5 sm:grid-cols-3">
          {steps.map((s, i) => (
            <div
              key={s.num}
              className="relative bg-white rounded-2xl border border-slate-200 p-5 lg:p-6"
              data-testid={`process-step-${i}`}
            >
              <div className="flex items-baseline gap-2.5">
                <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#94a3b8]">
                  Steg
                </span>
                <span className="text-4xl lg:text-[2.75rem] font-bold text-[#F5A65B] leading-none tracking-tight">
                  {s.num}
                </span>
              </div>
              <h3 className="mt-4 text-[17px] lg:text-lg font-bold text-[#0b1f27] leading-snug">
                {s.title}
              </h3>
              <p className="mt-2 text-[14px] text-[#334155] leading-relaxed">
                {s.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Recensioner ─────────────────────────────────────────────────────
function ReviewsSection() {
  const reviews = [
    {
      name: "Trädfällarna Laholm",
      text:
        "Vi använde Elido AB för att få hjälp med uppbyggnad av vår hemsida samt sökoptimering. Servicen va riktigt bra, dom förstod snabbt vad vi var ute efter och byggde upp allt helt enligt våra önskemål.",
    },
    {
      name: "Sara Ramsberg / Ramsberg Psykoterapi",
      text:
        "Superprofessionell. Kan området, är supertrevlig och snabb och kommer med förslag på förbättringar som jag inte ens bett om. Kan verkligen varmt rekommendera.",
    },
    {
      name: "Siricet / Rena Tjänster",
      text: "Mycket smidiga och snabba och utmärkt kvalitet.",
    },
  ];
  return (
    <section
      className="bg-white py-14 lg:py-20"
      data-testid="ny-hemsida-reviews"
    >
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="max-w-xl">
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
              Från Google
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.2rem] font-bold text-[#0b1f27] tracking-tight leading-[1.15]">
              Vad kunder säger
            </h2>
          </div>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackGoogleReviewsClick("reviews_section")}
            className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#255E69] hover:text-[#0e2f36] transition-colors"
            data-testid="reviews-google-link"
          >
            <Star className="w-4 h-4 fill-[#F5A65B] text-[#F5A65B]" aria-hidden="true" />
            <span>Läs på Google</span>
            <ExternalLink className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
        <div className="grid md:grid-cols-3 gap-4 lg:gap-5">
          {reviews.map((r, i) => (
            <blockquote
              key={i}
              className="rounded-2xl border border-slate-200 bg-[#FBF7F2] p-6 lg:p-7 flex flex-col"
              data-testid={`review-${i}`}
            >
              <div className="flex items-center gap-1 mb-3" aria-label="Betyg 5 av 5">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star
                    key={s}
                    className="w-4 h-4 fill-[#F5A65B] text-[#F5A65B]"
                    aria-hidden="true"
                  />
                ))}
              </div>
              <p className="text-[14.5px] text-[#1e293b] leading-relaxed italic">
                &ldquo;{r.text}&rdquo;
              </p>
              <footer className="mt-4 text-[13px] font-semibold text-[#0b1f27]">
                — {r.name}
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── FAQ (kort — 4 frågor) ────────────────────────────────────────────
function FaqSection() {
  const faqs = [
    {
      q: "Vad kostar en hemsida?",
      a: "En enklare specialdesignad företagshemsida börjar från 2 990 kr och en större sida från 5 990 kr. Behöver ni fler sidor, funktioner eller mer innehåll räknar vi fram ett fast pris innan arbetet börjar – ni får alltid priset innan ni bestämmer er.",
    },
    {
      q: "Hur lång tid tar det att få en ny hemsida?",
      a: "En enklare företagshemsida kan normalt tas fram snabbt när innehåll och underlag finns på plats. För större projekt sätter vi en tydlig tidsplan innan arbetet startar.",
    },
    {
      q: "Behöver vi ha texter och bilder färdiga?",
      a: "Nej. Vi hjälper till med struktur och texter och går igenom vilket material som behövs. Har ni redan bra bilder och innehåll använder vi självklart det.",
    },
    {
      q: "Måste vi byta domän eller e-post?",
      a: "Nej. En ny hemsida innebär normalt inte att ni förlorar er befintliga domän eller e-post. Vi hjälper till med rätt setup så att domän och e-post fortsätter fungera under och efter bytet.",
    },
    {
      q: "Kan ni hjälpa till med vår befintliga domän?",
      a: "Ja. Vi hjälper till med publicering och domänkoppling och ser till att befintlig e-post inte påverkas i onödan.",
    },
    {
      q: "Kan hemsidan byggas ut senare?",
      a: "Ja. Sidan kan kompletteras med fler undersidor, funktioner, formulär, SEO, språk eller andra lösningar när behovet växer.",
    },
    {
      q: "Vad händer efter att hemsidan publicerats?",
      a: "Elido kan sköta hosting, tekniskt underhåll och support. Ni behåller samtidigt ägandet av hemsidan och era konton.",
    },
  ];
  const [open, setOpen] = useState(-1);
  return (
    <section
      className="bg-[#FBF7F2] py-14 lg:py-20"
      data-testid="ny-hemsida-faq"
    >
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-8">
          <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
            FAQ
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.2rem] font-bold text-[#0b1f27] tracking-tight leading-[1.15]">
            Vanliga frågor
          </h2>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm divide-y divide-slate-200 overflow-hidden">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <details
                key={i}
                open={isOpen}
                onToggle={(e) => {
                  if (e.target.open) setOpen(i);
                  else if (isOpen) setOpen(-1);
                }}
                className="group"
                data-testid={`faq-item-${i}`}
              >
                <summary
                  className="cursor-pointer list-none px-5 lg:px-6 py-4 flex items-center justify-between gap-4 hover:bg-slate-50/60 transition-colors focus-visible:outline-none focus-visible:bg-slate-50"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="text-[15px] lg:text-[15.5px] font-semibold text-[#0b1f27]">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`w-4.5 h-4.5 shrink-0 text-[#255E69] transition-transform ${isOpen ? "rotate-180" : ""}`}
                    aria-hidden="true"
                  />
                </summary>
                <div className="px-5 lg:px-6 pb-4 -mt-1">
                  <p className="text-[14.5px] text-[#334155] leading-relaxed">
                    {f.a}
                  </p>
                </div>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Formulär ────────────────────────────────────────────────────────
// Trygghetsruta som sammanfattar processen precis före formuläret.
// Håller löftena i linje med ProcessSection (fem arbetsdagar för första
// klickbara demon för relevanta projekt) — ingen ny garanti läggs till.
function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    current_website: "",
    message: "",
    // honeypot — dolt fält, ska alltid vara tomt
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [showOptional, setShowOptional] = useState(false);
  // Öppna automatiskt om ett frivilligt fält får valideringsfel, så att
  // felet aldrig kan hamna utanför synfältet.
  const optionalOpen = showOptional || Boolean(errors.company);
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const inputRefs = useRef({});
  const successRef = useRef(null);

  // Fånga upp attribution när komponenten mountas (första-touch persist)
  useEffect(() => {
    captureAttribution();
  }, []);

  // När success visas — flytta fokus till success-meddelandet
  useEffect(() => {
    if (state === "success" && successRef.current) {
      // liten timeout så att elementet hunnit renderas
      const t = setTimeout(() => successRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [state]);

  const setField = (key) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Fyll i ert namn";
    if (!form.email.trim()) next.email = "Fyll i e-post";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Kontrollera e-postadressen";
    setErrors(next);
    return next;
  };

  const focusFirstError = (nextErrors) => {
    const order = ["name", "email"];
    const first = order.find((k) => nextErrors[k]);
    if (first && inputRefs.current[first]) {
      try {
        inputRefs.current[first].focus();
      } catch { /* ignore */ }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (state === "submitting") return;
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      toast.error("Kontrollera de obligatoriska fälten");
      return;
    }
    setState("submitting");
    setErrorMsg("");

    const marketingConsent = !!(getConsent() && getConsent().marketing);
    // Kör en sista captureAttribution() precis före getAttribution() så att
    // URL-parametrar (gclid/fbclid/utm_*) fångas som fallback ifall
    // consent-listenern inte hann köra före submit. captureAttribution() är
    // idempotent och first-touch skyddat, så existerande värden skrivs inte
    // över.
    if (marketingConsent) {
      captureAttribution();
    }
    // getAttribution() själv returnerar {} utan marketing-consent, men
    // kombinera timestamp först här så payloaden aldrig läcker attribution
    // från en tidigare session med consent.
    const latestAttribution = marketingConsent
      ? { ...getAttribution(), timestamp: new Date().toISOString() }
      : {};

    let res;
    let data = {};
    try {
      res = await fetch(LEAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          current_website: form.current_website.trim(),
          message: form.subject
            ? `Vad gäller det? ${form.subject}`
            : form.message.trim(),
          website: form.website, // honeypot
          attribution: latestAttribution,
          // Skickar användarens marketing-consent till backend så CAPI
          // Lead endast avfyras när användaren har godkänt marketing.
          marketing_consent: marketingConsent,
          // Lead-driftdata: vilket samtyckesval gällde vid submit. Backend
          // härleder attribution_status från dessa + attributionen.
          ...getConsentLeadFields(),
        }),
      });
      data = await res.json().catch(() => ({}));
    } catch (err) {
      setErrorMsg("Kunde inte ansluta till servern. Kontrollera nätverket och försök igen.");
      setState("error");
      toast.error("Kunde inte skicka. Försök igen.");
      return;
    }

    // KORREKT KONTRAKT:
    //  • Endast `accepted === true` räknas som accepterat lead.
    //  • Alla andra svar (honeypot 200 med accepted=false, 422 ogiltig
    //    e-post, 429 rate-limit, 5xx server) ska INTE visa tackläget
    //    och INTE skicka form_success.
    if (data && data.accepted === true) {
      trackFormSuccess({
        ny_hemsida_lead_source: "form",
        ny_hemsida_lead_id: data.lead_id,
      });
      setState("success");
      return;
    }

    // Ej accepterat → visa fel-state (utom när honeypot triggat,
    // då diskutera vi inte anledningen men vi lämnar sidan intakt).
    if (res && res.ok && data && data.accepted === false) {
      // Honeypot / bot-fall: behåll formuläret men signalera inte
      // konvertering och visa ingen "tack"-toast.
      setState("idle");
      return;
    }

    // Robust extraktion av felmeddelande. Både FastAPI-rate-limit-
    // middleware (`{detail: {error, retry_after}}`) och våra egna
    // endpoints (`{error: "..."}`, `{detail: "..."}`) hanteras.
    const extractError = () => {
      if (!data) return null;
      if (typeof data.error === "string" && data.error) return data.error;
      if (typeof data.detail === "string" && data.detail) return data.detail;
      if (data.detail && typeof data.detail === "object") {
        if (typeof data.detail.error === "string" && data.detail.error) return data.detail.error;
        // Pydantic validation errors — array av `{msg, loc, ...}`
        if (Array.isArray(data.detail) && data.detail[0] && data.detail[0].msg) {
          return "Kontrollera de obligatoriska fälten och e-postadressen.";
        }
      }
      return null;
    };
    const msg =
      extractError() ||
      (res && res.status === 422 && "Kontrollera de obligatoriska fälten och e-postadressen.") ||
      (res && res.status === 429 && "Vänta en stund innan du försöker igen.") ||
      "Något gick fel. Försök igen om en stund.";
    setErrorMsg(String(msg));
    setState("error");
    toast.error(String(msg));
  };

  return (
    <section
      id="formular"
      className="bg-[#0e2f36] text-white py-12 sm:py-16 lg:py-24 scroll-mt-24 relative overflow-hidden"
      data-testid="ny-hemsida-form-section"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(700px 300px at 90% 0%, rgba(93,188,203,0.14) 0%, transparent 55%), radial-gradient(700px 300px at 10% 100%, rgba(245,166,91,0.08) 0%, transparent 60%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Vänsterkolumn: personlig kontakt */}
          <div className="lg:col-span-5">
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#5DBCCB] mb-3">
              Personlig kontakt
            </div>
            <h2 className="text-[26px] leading-[1.15] sm:text-4xl lg:text-[2.2rem] font-bold tracking-tight text-white">
              Få ett konkret prisförslag för ert företag
            </h2>
            <p className="mt-4 text-[15px] sm:text-[15.5px] text-white/80 leading-relaxed">
              Berätta kort vad ni behöver, så återkommer vi personligen med
              pris, upplägg och nästa steg. Kostnadsfritt och utan köpkrav – ni
              pratar direkt med oss som bygger.
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-lg" data-testid="form-contact-people">
              {[
                {
                  src: "david-siekas.png",
                  name: "David Siekas",
                  alt: "David Siekas, Elido AB",
                  phone: "072-370 81 58",
                  tel: "+46723708158",
                  email: "david@elido.nu",
                },
                {
                  src: "filip-imberg.jpeg",
                  name: "Filip Imberg",
                  alt: "Filip Imberg, Elido AB",
                  phone: "070-734 84 84",
                  tel: "+46707348484",
                  email: "filip@elido.nu",
                },
              ].map((p) => (
                <div key={p.name} data-testid={`contact-person-${p.name.split(" ")[0].toLowerCase()}`}>
                  <div className="flex items-center gap-3">
                    <img
                      src={`/img/ny-hemsida/${p.src}`}
                      alt={p.alt}
                      width="160"
                      height="160"
                      loading="lazy"
                      decoding="async"
                      className="w-14 h-14 rounded-full object-cover border border-white/15 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.5)] shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-[14.5px] font-semibold text-white leading-tight">
                        {p.name}
                      </div>
                      <div className="text-[13px] text-white/70 leading-tight mt-0.5">
                        Elido AB
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-col gap-1 text-[14px]">
                    <a
                      href={`tel:${p.tel}`}
                      className="text-[#5DBCCB] hover:text-white transition-colors"
                    >
                      {p.phone}
                    </a>
                    <a
                      href={`mailto:${p.email}`}
                      className="text-[#5DBCCB] hover:text-white transition-colors break-all"
                    >
                      {p.email}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Högerkolumn: formulär */}
          <div className="lg:col-span-7">
        {state === "success" ? (
          <div
            ref={successRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className="rounded-2xl bg-white/[0.04] border border-white/15 p-6 sm:p-8 lg:p-10 text-center focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F5A65B]/40"
            data-testid="form-success"
          >
            <div className="w-14 h-14 rounded-full bg-[#F5A65B]/20 border border-[#F5A65B]/40 flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7 text-[#F5A65B]" aria-hidden="true" />
            </div>
            <h3 className="text-xl font-semibold">Tack – förfrågan är mottagen</h3>
            <p className="mt-2 text-white/80">
              Vi återkommer personligen med pris, upplägg och nästa steg. Inget
              köp och inget samtal är bokat.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            noValidate
            className="rounded-2xl bg-white/[0.04] border border-white/15 backdrop-blur p-4 sm:p-6 lg:p-8 space-y-3 sm:space-y-4"
            data-testid="ny-hemsida-form"
            onFocus={() => trackFormStart()}
            aria-describedby="form-help"
          >
            <p
              id="form-help"
              className="text-[13px] sm:text-[13.5px] text-white/70 leading-relaxed"
              data-testid="form-required-note"
            >
              Namn och e-post räcker. Övriga fält är frivilliga.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <FormRow
                id="lead-name"
                label="Namn"
                required
                error={errors.name}
                value={form.name}
                onChange={setField("name")}
                autoComplete="name"
                testId="form-name"
                maxLength={120}
                inputRef={(el) => (inputRefs.current.name = el)}
              />
              <FormRow
                id="lead-email"
                label="E-post"
                type="email"
                required
                error={errors.email}
                value={form.email}
                onChange={setField("email")}
                autoComplete="email"
                testId="form-email"
                maxLength={254}
                inputRef={(el) => (inputRefs.current.email = el)}
              />
            </div>

            {/* Sekundär expandering. Fälten ligger kvar i DOM:en (hidden) så
                refs, värden och validering är oförändrade — endast UI. */}
            <button
              type="button"
              onClick={() => setShowOptional((v) => !v)}
              aria-expanded={optionalOpen}
              aria-controls="lead-optional-fields"
              className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-white/80 hover:text-white underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5DBCCB]/40 rounded"
              data-testid="form-optional-toggle"
            >
              <ChevronDown
                className={
                  "w-4 h-4 transition-transform duration-200 " +
                  (optionalOpen ? "rotate-180" : "")
                }
                aria-hidden="true"
              />
              Lägg till fler uppgifter (valfritt)
            </button>

            <div
              id="lead-optional-fields"
              hidden={!optionalOpen}
              data-testid="form-optional-fields"
            >
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <FormRow
                    id="lead-company"
                    label="Företag"
                    optional
                    error={errors.company}
                    value={form.company}
                    onChange={setField("company")}
                    autoComplete="organization"
                    testId="form-company"
                    maxLength={160}
                    inputRef={(el) => (inputRefs.current.company = el)}
                  />
                  <FormRow
                    id="lead-phone"
                    label="Telefon"
                    type="tel"
                    optional
                    value={form.phone}
                    onChange={setField("phone")}
                    autoComplete="tel"
                    testId="form-phone"
                    maxLength={40}
                  />
                </div>
                <div>
                  <label
                    htmlFor="lead-subject"
                    className="block text-[11px] font-semibold text-white/70 mb-1.5 sm:mb-2 tracking-[0.12em] uppercase"
                  >
                    Vad gäller det?
                    <span className="ml-2 normal-case tracking-normal text-white/50 font-normal">
                      (frivilligt)
                    </span>
                  </label>
                  <select
                    id="lead-subject"
                    value={form.subject}
                    onChange={setField("subject")}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white text-[15px] focus:outline-none focus:border-[#5DBCCB]/70 focus:ring-2 focus:ring-[#5DBCCB]/25 transition-colors appearance-none"
                    data-testid="form-subject"
                  >
                    <option value="" className="text-[#0b1f27]">Välj ett alternativ</option>
                    <option value="Ny hemsida" className="text-[#0b1f27]">Ny hemsida</option>
                    <option value="Förbättra befintlig" className="text-[#0b1f27]">Förbättra befintlig</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Honeypot — dolt för människor och screenreaders */}
            <div
              aria-hidden="true"
              className="absolute -left-[9999px] w-px h-px overflow-hidden"
              style={{ position: "absolute" }}
            >
              <label htmlFor="lead-website">Webbplats</label>
              <input
                id="lead-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={setField("website")}
              />
            </div>

            {errorMsg ? (
              <div
                className="text-[14px] text-[#FCA5A5] bg-[#7F1D1D]/30 border border-[#F87171]/25 rounded-xl px-4 py-3"
                data-testid="form-error"
                role="alert"
              >
                {errorMsg}
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-1">
              <button
                type="submit"
                disabled={state === "submitting"}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3 sm:py-3.5 rounded-full font-semibold text-[15px] tracking-tight bg-[#F5A65B] hover:bg-[#eb9843] text-[#0b1f27] shadow-[0_10px_28px_-8px_rgba(245,166,91,0.55)] transition-all duration-200 hover:translate-y-[-1px] disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F5A65B]/40"
                data-testid="form-submit"
              >
                {state === "submitting" ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                )}
                {state === "submitting" ? "Skickar…" : <span className="whitespace-nowrap">Få mitt prisförslag</span>}
              </button>
              <p className="text-[12px] sm:text-[12.5px] text-white/60 leading-relaxed text-center sm:text-left">
                Genom att skicka godkänner ni att vi kontaktar er. Läs vår{" "}
                <Link
                  to="/integritetspolicy"
                  className="underline decoration-white/40 underline-offset-2 hover:decoration-white text-white/80"
                >
                  integritetspolicy
                </Link>
                .
              </p>
            </div>
          </form>
        )}
          </div>
        </div>
      </div>
    </section>
  );
}

function FormRow({
  id,
  label,
  required,
  optional,
  error,
  value,
  onChange,
  type = "text",
  placeholder,
  autoComplete,
  testId,
  maxLength,
  inputRef,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[11px] font-semibold text-white/70 mb-1.5 sm:mb-2 tracking-[0.1em] sm:tracking-[0.12em] uppercase"
      >
        {label}
        {required ? (
          <>
            <span className="ml-1 text-[#F5A65B]" aria-hidden="true">*</span>
            <span className="sr-only"> (obligatoriskt)</span>
          </>
        ) : optional ? (
          <span className="ml-2 normal-case tracking-normal text-white/50 font-normal">
            (frivilligt)
          </span>
        ) : null}
      </label>
      <input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={!!required}
        aria-required={required ? "true" : undefined}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${id}-error` : undefined}
        maxLength={maxLength}
        className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white placeholder-white/40 text-[15px] focus:outline-none focus:ring-2 transition-colors ${error
          ? "border-[#F87171]/60 focus:border-[#F87171] focus:ring-[#F87171]/25"
          : "border-white/15 focus:border-[#5DBCCB]/70 focus:ring-[#5DBCCB]/25"
        }`}
        data-testid={testId}
      />
      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1.5 text-[12.5px] text-[#FCA5A5]"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ─── Personerna bakom Elido ──────────────────────────────────────────
const ELIDO_PEOPLE = [
  {
    src: "david-siekas.png",
    name: "David Siekas",
    role: "Bygger & utvecklar",
    alt: "David Siekas, Elido AB",
    phone: "072-370 81 58",
    tel: "+46723708158",
    email: "david@elido.nu",
  },
  {
    src: "filip-imberg.jpeg",
    name: "Filip Imberg",
    role: "Bygger & utvecklar",
    alt: "Filip Imberg, Elido AB",
    phone: "070-734 84 84",
    tel: "+46707348484",
    email: "filip@elido.nu",
  },
];

function PeopleSection() {
  return (
    <section className="bg-white py-14 lg:py-20" data-testid="ny-hemsida-people">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#255E69] mb-3">
            Personerna bakom Elido
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[2.2rem] font-bold text-[#0b1f27] tracking-tight leading-[1.15]">
            Ni vet vem ni pratar med
          </h2>
        </div>
        <div className="mt-9 grid gap-5 sm:grid-cols-2 max-w-2xl" data-testid="people-list">
          {ELIDO_PEOPLE.map((p) => (
            <div
              key={p.name}
              className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-[#FBF7F2] p-5"
              data-testid={`person-${p.name.split(" ")[0].toLowerCase()}`}
            >
              <img
                src={`/img/ny-hemsida/${p.src}`}
                alt={p.alt}
                width="160"
                height="160"
                loading="lazy"
                decoding="async"
                className="w-16 h-16 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <div className="min-w-0">
                <div className="text-[15px] font-bold text-[#0b1f27] leading-tight">
                  {p.name}
                </div>
                <div className="text-[13px] text-[#475569] leading-tight mt-0.5">
                  Elido AB · {p.role}
                </div>
                <div className="mt-2 flex flex-col gap-0.5 text-[13.5px]">
                  <a href={`tel:${p.tel}`} className="text-[#255E69] hover:text-[#0e2f36] transition-colors">
                    {p.phone}
                  </a>
                  <a href={`mailto:${p.email}`} className="text-[#255E69] hover:text-[#0e2f36] transition-colors break-all">
                    {p.email}
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Stark slut-CTA ──────────────────────────────────────────────────
function FinalCta() {
  return (
    <section
      className="bg-[#0b1f27] text-white py-16 lg:py-24"
      data-testid="ny-hemsida-final-cta"
    >
      <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.1]">
          Redo att komma igång?
        </h2>
        <p className="mt-4 text-[15px] sm:text-[16px] text-white/80 leading-relaxed max-w-xl mx-auto">
          Få ett konkret prisförslag för ert företag – fast pris, ni äger sidan
          och ingen bindningstid. Kostnadsfritt och utan köpkrav.
        </p>
        <div className="mt-8 flex justify-center">
          <a
            href="#formular"
            onClick={(e) => {
              trackCtaClick("final_cta");
              if (smoothScrollTo("#formular")) e.preventDefault();
            }}
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-[15px] tracking-tight bg-[#F5A65B] hover:bg-[#eb9843] text-[#0b1f27] shadow-[0_14px_32px_-8px_rgba(245,166,91,0.55)] transition-all duration-200 hover:translate-y-[-1px] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F5A65B]/40"
            data-testid="final-cta-link"
          >
            Få mitt prisförslag
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
        <p className="mt-4 text-[12.5px] text-white/55">
          5/5 på Google · Fast pris · Ni äger sidan · Ingen bindningstid
        </p>
      </div>
    </section>
  );
}

// ─── Sidan ──────────────────────────────────────────────────────────
export default function NyHemsidaPage() {
  // Kör attribution-fångst så tidigt som möjligt (även innan formuläret mountas)
  useEffect(() => {
    captureAttribution();
    // Consent Mode v2: om användaren accepterar marketing EFTER mount hoppar
    // captureAttribution() ut tomhänt vid initialkörningen. Kör därför om den
    // så snart CONSENT_EVENTS.UPDATED indikerar marketing=true — annars
    // hamnar leadet i direct_or_unattributed när URL:en fortfarande har
    // gclid/fbclid/utm_*.
    const onConsent = (e) => {
      if (e && e.detail && e.detail.marketing === true) {
        captureAttribution();
      }
    };
    window.addEventListener(CONSENT_EVENTS.UPDATED, onConsent);
    return () => window.removeEventListener(CONSENT_EVENTS.UPDATED, onConsent);
  }, []);

  // Mjuk scroll-reveal för sektionerna under heron. Basläget (opacity 0)
  // sätts i index.css så att inget "blinkar" innan JS hunnit köra.
  useEffect(() => {
    const els = Array.from(
      document.querySelectorAll(
        '[data-testid="ny-hemsida-page"] main > section:not([data-testid="ny-hemsida-hero"])'
      )
    );
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Efter att Helmet hunnit spegla in sina taggar — kör dedupeSeoTags()
  // så att den prerenderade kopian tas bort. Använder två frames + en
  // extra tick eftersom react-helmet-async committar via requestAnimation
  // Frame och vi vill inte tävla med det. Detta gör att sidan slutar med
  // exakt en title / canonical / robots / description.
  useEffect(() => {
    let raf1, raf2, timeoutId;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        try {
          dedupeSeoTags();
        } catch { /* aldrig krascha på dedupe */ }
      });
    });
    // Safety net — om Helmet av någon anledning inte hann → försök igen
    timeoutId = setTimeout(() => {
      try {
        dedupeSeoTags();
      } catch { /* ignore */ }
    }, 250);
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <>
      <Helmet>
        {/* GlobalSEOHead exkluderar /ny-hemsida — vi äger head:en här. */}
        <title>Ny hemsida för företag – från 2 990 kr | Elido</title>
        <meta
          name="description"
          content="Professionell hemsida för företag från 2 990 kr. Fast pris, ingen bindningstid och ni äger sidan. Få ett konkret prisförslag från Elido."
        />
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href="https://elido.nu/ny-hemsida" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sv_SE" />
        <meta property="og:title" content="Ny hemsida för företag – från 2 990 kr | Elido" />
        <meta
          property="og:description"
          content="Professionell hemsida för företag från 2 990 kr. Fast pris, ingen bindningstid och ni äger sidan. Få ett konkret prisförslag från Elido."
        />
        <meta property="og:url" content="https://elido.nu/ny-hemsida" />
        <meta property="og:image" content="https://elido.nu/img/ny-hemsida/tradfallarna-live-1280.webp" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Preload för LCP-bild (hero-foto) — WebP, mobil-crop vs desktop */}
        <link
          rel="preload"
          as="image"
          href="/img/ny-hemsida/david-filip-1280.webp"
          imageSrcSet="/img/ny-hemsida/david-filip-800.webp 800w, /img/ny-hemsida/david-filip-1280.webp 1280w"
          imageSizes="(min-width: 1024px) 45vw, 92vw"
          media="(min-width: 1024px)"
        />
        <link
          rel="preload"
          as="image"
          href="/img/ny-hemsida/david-filip-mobile-640.webp"
          imageSrcSet="/img/ny-hemsida/david-filip-mobile-640.webp 640w, /img/ny-hemsida/david-filip-mobile-960.webp 960w"
          imageSizes="92vw"
          media="(max-width: 1023px)"
        />
      </Helmet>

      <div
        className="bg-white text-[#0b1f27] font-['Inter'] antialiased"
        data-testid="ny-hemsida-page"
      >
        <LandingHeader />
        <main>
          <Hero />
          <TrustLogoCloud />
          <TrustStrip />
          <CustomerCases />
          <ReviewsSection />
          <LeadForm />
          <PricingSection />
          <ProcessSection />
          <WhyElidoSection />
          <PeopleSection />
          <FaqSection />
          <FinalCta />
        </main>
        <NexusFooter />
        <CookieConsent />
      </div>
    </>
  );
}
